import { SignInOptions } from 'next-auth/react';
import { TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import * as ApplicationConfigurationService from 'src/services/config/ApplicationConfigurationService.client';
import { zillowSignIn, ZillowSignInOptions } from './zillowSignIn/zillowSignIn';

declare global {
    // eslint-disable-next-line no-var
    var SESSION_START_REQUESTED: boolean;
    // eslint-disable-next-line no-var
    var MFA_SESSION_START_REQUESTED: boolean;
}

/**
 * Since multiple components and business logic paths can cause a sign in attempt,
 * it can be difficult to debug why session logins were attempted. To address this, this function
 * requires that a justification for performing a login is provided, which is then logged to the console in lower
 * environments for debugging.
 *
 * Additionally, this function abstracts the logic around ensuring we leverage the required "signin bounce" experience
 * that is aimed at resolving the back-button-breaking-on-authorization-code-flow-pages problem.
 *
 * The function determines the callback URL by:
 * 1. Checking for existing callbackUrl query parameters to prevent infinite loops
 * 2. Validating callback URLs against an allowlist for security
 * 3. Falling back to the current pathname if no valid callback is found
 *
 * @param justification - why the session should be started
 * @param experiments - active experiments/feature flags
 * @param signInOptions - NextAuth sign-in options (including mfaRequired for step-up auth)
 * @param authorizationParams - additional authorization parameters
 */
export const handleSessionStart = (
    justification: string,
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>,
    signInOptions: ZillowSignInOptions = {},
    authorizationParams: Record<string, string> = {}
) => {
    const isMfaRequest = signInOptions.mfaRequired === true;

    // Use separate flags for regular auth and MFA step-up to prevent:
    // 1. Duplicate regular auth requests during the same auth flow
    // 2. Duplicate MFA requests during the same MFA flow
    // But allow MFA step-up after regular auth (different auth types)
    // Flags are reset when user returns from auth (via beforeunload) or after timeout
    if (isMfaRequest) {
        if (globalThis.MFA_SESSION_START_REQUESTED) {
            return;
        }
        globalThis.MFA_SESSION_START_REQUESTED = true;
        // Reset flag when user leaves the page (going to Auth0)
        const resetMfaFlag = () => {
            globalThis.MFA_SESSION_START_REQUESTED = false;
            globalThis.removeEventListener('beforeunload', resetMfaFlag);
        };
        globalThis.addEventListener('beforeunload', resetMfaFlag);
        // Also reset after timeout as fallback
        setTimeout(() => {
            globalThis.MFA_SESSION_START_REQUESTED = false;
            globalThis.removeEventListener('beforeunload', resetMfaFlag);
        }, 5000);
    } else {
        if (globalThis.SESSION_START_REQUESTED) {
            return;
        }
        globalThis.SESSION_START_REQUESTED = true;
        // Reset flag when user leaves the page (going to Auth0)
        const resetAuthFlag = () => {
            globalThis.SESSION_START_REQUESTED = false;
            globalThis.removeEventListener('beforeunload', resetAuthFlag);
        };
        globalThis.addEventListener('beforeunload', resetAuthFlag);
        // Also reset after timeout as fallback
        setTimeout(() => {
            globalThis.SESSION_START_REQUESTED = false;
            globalThis.removeEventListener('beforeunload', resetAuthFlag);
        }, 5000);
    }
    const configService = ApplicationConfigurationService.ConfigProvider.getInstance();
    if (configService.runtimeConfig.isLowerEnvironment) {
        // eslint-disable-next-line no-console
        console.info('Application Session Started - ', justification);
    }

    /**
     * Determines the appropriate callback URL for authentication.
     * Priority:
     * 1. Use callbackUrl from signInOptions if explicitly provided (e.g., MFA step-up with mfa_completed param)
     * 2. Check for existing callbackUrl query parameter to prevent infinite loops from signin bounce page
     * 3. Fall back to current pathname
     */
    let callbackUrl = signInOptions.callbackUrl;

    if (!callbackUrl) {
        const urlParams = new URLSearchParams(globalThis.location.search);
        const existingCallbackUrl = urlParams.get('callbackUrl');
        // Remove callbackUrl from search params to avoid including it in the fallback URL
        // This preserves other query params (like activation tokens) while preventing
        // the invalid callbackUrl from being included in the fallback
        urlParams.delete('callbackUrl');
        const cleanSearch = urlParams.toString() ? `?${urlParams.toString()}` : '';
        callbackUrl = configService.routeConfiguration.getSafeCallbackUrl(
            existingCallbackUrl,
            globalThis.location.pathname + cleanSearch
        );
    }

    const updatedSignInOptions: SignInOptions = {
        ...signInOptions,
        callbackUrl,
    };

    zillowSignIn(experiments, updatedSignInOptions, authorizationParams);
};
